import { GameState, IPiece, Position, PieceType, MoveType } from "../../types";
import { MCTSUtils_v2, cloneFullGameState } from "../utils/MCTSUtils";
import { MCTSMoveGenerationService } from "../utils/MCTSMoveGenerationService";

/**
 * MCTS Evaluation Module
 * Handles position scoring for simulations: Material + Formation + Advancement + Entanglement + Tactics + RNG Variance
 * Modular for easy tuning based on feedback.
 */
export class MCTSEvaluation {
  private config: {
    materialWeight: number;
    formationWeight: number;
    advancementWeight: number;
    entanglementWeight: number;
    tacticsWeight: number; // New: Forks/pins
    rngVariancePenalty: number; // New: Penalize uncertainty
  };

  constructor(config: any) {
    this.config = {
      materialWeight: config.materialWeight || 200,
      formationWeight: config.formationWeight || 150,
      advancementWeight: config.advancementWeight || 50,
      entanglementWeight: config.entanglementWeight || 20,
      tacticsWeight: 100, // New default
      rngVariancePenalty: 20, // New default
    };
  }

  /**
   * Main eval: Normalized [0,1] score for player (higher better)
   */
  public evaluatePosition(state: GameState, isLight: boolean): number {
    let score = 0;

    // 1. Material Balance
    score +=
      this.calculateMaterialBalance(state, isLight) *
      this.config.materialWeight;

    // 2. Formation Score (support + defense)
    score +=
      this.calculateFormationScore(state, isLight) *
      this.config.formationWeight;

    // 3. Advancement Score (forward + control)
    score +=
      this.calculateAdvancementScore(state, isLight) *
      this.config.advancementWeight;

    // 4. Entanglement Score
    score +=
      this.calculateEntanglementScore(state, isLight) *
      this.config.entanglementWeight;

    // 5. Tactics Score (forks/pins/threats)
    score +=
      this.calculateTacticsScore(state, isLight) * this.config.tacticsWeight;

    // 6. RNG Variance Penalty (sample 20 sims for uncertainty)
    const variance = this.calculateRNGVariance(state, isLight);
    score -= variance * this.config.rngVariancePenalty;

    // Normalize to [0,1]
    return Math.max(0, Math.min(1, score / 1000)); // Adjust denom based on weights
  }

  // Existing methods from FocusedMCTS (moved here)
  private calculateMaterialBalance(state: GameState, isLight: boolean): number {
    let ourPieces = 0;
    let enemyPieces = 0;

    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const pieces = state.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === isLight) {
            ourPieces += this.getPieceValue(piece); // Enhanced: Weighted by type
          } else {
            enemyPieces += this.getPieceValue(piece);
          }
        }
      }
    }

    return ourPieces - enemyPieces;
  }

  private calculateFormationScore(state: GameState, isLight: boolean): number {
    let formationScore = 0;

    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const pieces = state.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === isLight) {
            const supportCount = this.countAdjacentFriendlyPieces(
              state,
              { row, col },
              isLight
            );
            formationScore += supportCount * 2; // Mutual support
            // Bonus for defended advances (Priority 26)
            if (this.isAdvancedPosition(row, isLight))
              formationScore += supportCount * 3;
          }
        }
      }
    }

    return formationScore;
  }

  private calculateAdvancementScore(
    state: GameState,
    isLight: boolean
  ): number {
    let advancementScore = 0;
    const targetRow = isLight ? 7 : 0;

    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const pieces = state.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === isLight) {
            const distanceToTarget = Math.abs(row - targetRow);
            const advancement = 7 - distanceToTarget;
            advancementScore += advancement;
            // Bonus for controlling middle (rows 3-4)
            if (row >= 3 && row <= 4) advancementScore += 10;
          }
        }
      }
    }

    return advancementScore;
  }

  private calculateEntanglementScore(
    state: GameState,
    isLight: boolean
  ): number {
    let entanglementScore = 0;

    for (let row = 0; row < state.board.length; row++) {
      for (let col = 0; col < state.board[row].length; col++) {
        const pieces = state.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isEntangled) {
            entanglementScore += piece.isLight === isLight ? -3 : 3;
          }
          if (piece.isLight === isLight && piece.type === PieceType.Octopus) {
            entanglementScore +=
              this.countEntanglementOpportunities(
                state,
                { row, col },
                isLight
              ) * 0.5;
          }
          if (piece.isLight === isLight && !piece.isEntangled) {
            if (this.isVulnerableToEntanglement(state, { row, col }, isLight)) {
              entanglementScore -= 0.5;
            }
          }
        }
      }
    }

    return entanglementScore;
  }

  // New: Tactics (forks/pins/threats) - Basic threat count
  private calculateTacticsScore(state: GameState, isLight: boolean): number {
    // TEMPORARILY DISABLED: This was causing stack overflow via MonkeyMovement.getValidJumpMoves
    // by calling MCTSMoveGenerationService.getAllMoves during evaluation
    return 0;

    /*
    let tacticsScore = 0;
    // Sim simple threats: Count attacked enemy pieces (Priority 19/21)
    const moves = MCTSMoveGenerationService.getInstance().getAllMoves(state); // ❌ STACK OVERFLOW!
    for (const move of moves) {
      if (move.move.moveType === MoveType.capture) tacticsScore += 20; // Fork-like if multi-threat
    }
    return tacticsScore;
    */
  }

  // New: RNG Variance (sample 20 sims, std dev of scores)
  private calculateRNGVariance(state: GameState, isLight: boolean): number {
    // TEMPORARILY DISABLED: This was causing infinite recursion by calling evaluatePosition
    // which calls calculateRNGVariance again. Need to implement without recursive evaluation.
    return 0;

    /*
    const samples = 20;
    const scores: number[] = [];
    for (let i = 0; i < samples; i++) {
      const simState = this.simulateRNGVariant(state); // Apply random (e.g., Monkey roll)
      scores.push(this.evaluatePosition(simState, isLight)); // ❌ INFINITE RECURSION!
    }
    const mean = scores.reduce((a, b) => a + b, 0) / samples;
    const variance =
      scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / samples;
    return Math.sqrt(variance); // Std dev as uncertainty
    */
  }

  private simulateRNGVariant(state: GameState): GameState {
    const variant = cloneFullGameState(state);
    // Example: For Monkey captures, roll prob and apply if success
    // Expand for your RNG elements (Snake respawn always, but Monkey 1/3)
    return variant;
  }

  // Helper: Piece values for material (custom per your rules)
  private getPieceValue(piece: IPiece): number {
    const values: Record<PieceType, number> = {
      [PieceType.Bear]: 95, // Strong defensive abilities
      [PieceType.Crane]: 70, // Balanced movement
      [PieceType.Frog]: 60, // Defensive lily pad control
      [PieceType.Fungi]: 40, // Support/utility piece
      [PieceType.Goat]: 85, // Traverse ability
      [PieceType.Monkey]: 75, // RNG capture with bonus turns
      [PieceType.Octopus]: 80, // Entanglement control
      [PieceType.Penguin]: 80, // Fast racer, weak capture
      [PieceType.Rhino]: 90, // Strong charge abilities
      [PieceType.Snake]: 85, // Extra life with shed
      [PieceType.Tree]: 30, // Housing utility
      [PieceType.Virus]: 65, // Infection abilities
      [PieceType.Wolf]: 90, // Balanced strong piece
    };
    return values[piece.type] || 50; // Default fallback
  }

  // Helper methods for adjacency and positioning
  private countAdjacentFriendlyPieces(
    state: GameState,
    position: Position,
    isLight: boolean
  ): number {
    let count = 0;
    const directions = [
      { row: -1, col: -1 },
      { row: -1, col: 0 },
      { row: -1, col: 1 },
      { row: 0, col: -1 },
      { row: 0, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 0 },
      { row: 1, col: 1 },
    ];

    for (const dir of directions) {
      const newRow = position.row + dir.row;
      const newCol = position.col + dir.col;

      if (
        newRow >= 0 &&
        newRow < state.board.length &&
        newCol >= 0 &&
        newCol < state.board[0].length
      ) {
        const pieces = state.board[newRow][newCol].Pieces;
        for (const piece of pieces) {
          if (piece.isLight === isLight) {
            count++;
          }
        }
      }
    }

    return count;
  }

  private isAdvancedPosition(row: number, isLight: boolean): boolean {
    return isLight ? row >= 4 : row <= 3;
  }

  // Entanglement opportunity counting
  private countEntanglementOpportunities(
    state: GameState,
    position: Position,
    isLight: boolean
  ): number {
    let opportunities = 0;
    const directions = [
      { row: -1, col: -1 },
      { row: -1, col: 0 },
      { row: -1, col: 1 },
      { row: 0, col: -1 },
      { row: 0, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 0 },
      { row: 1, col: 1 },
    ];

    for (const dir of directions) {
      const newRow = position.row + dir.row;
      const newCol = position.col + dir.col;

      if (
        newRow >= 0 &&
        newRow < state.board.length &&
        newCol >= 0 &&
        newCol < state.board[0].length
      ) {
        const pieces = state.board[newRow][newCol].Pieces;
        for (const piece of pieces) {
          if (piece.isLight !== isLight && !piece.isEntangled) {
            opportunities++;
          }
        }
      }
    }

    return opportunities;
  }

  // Check if a piece is vulnerable to entanglement
  private isVulnerableToEntanglement(
    state: GameState,
    position: Position,
    isLight: boolean
  ): boolean {
    const directions = [
      { row: -1, col: -1 },
      { row: -1, col: 0 },
      { row: -1, col: 1 },
      { row: 0, col: -1 },
      { row: 0, col: 1 },
      { row: 1, col: -1 },
      { row: 1, col: 0 },
      { row: 1, col: 1 },
    ];

    for (const dir of directions) {
      const newRow = position.row + dir.row;
      const newCol = position.col + dir.col;

      if (
        newRow >= 0 &&
        newRow < state.board.length &&
        newCol >= 0 &&
        newCol < state.board[0].length
      ) {
        const pieces = state.board[newRow][newCol].Pieces;
        for (const piece of pieces) {
          if (piece.isLight !== isLight && piece.type === PieceType.Octopus) {
            return true;
          }
        }
      }
    }

    return false;
  }
}
